/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable max-len */
/*
 * Copyright (C) iSchoolConnect - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { Icon, Text } from '@isc/chatui-core';
import '@isc/chatui-core/es/styles/index.less';
import '@isc/chatui-core/dist/index.css';
import './Header.css';
import '../../isc-icons.css';
import Avatar from 'react-avatar';
import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Menu, MenuItem, Tooltip } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { PushNotificationService } from '../../services/PushNotificationService';
import { Utils } from '../../services/Utils';
import { UIService } from '../../services/UIService';
import { DEVICE_TYPE, ParsedUser } from '../../types';
import { ApiService } from '../../services/ApiService';
import { USER_ROLES } from '../../enums/user-roles.enum';
import { STATUS } from '../../enums/status.enum';
import { LoggerService } from '../../services/LoggerService';
import { Loader } from '../Loader/Loader';
import { StorageService } from '../../services/StorageService';

interface props {
	user?: { imageUrl?: string; name?: string } | null;
	actionsHandler?: ((action: string) => void) | null;
	allowClose?: boolean;
	allowResize?: boolean;
	allowPrevious?: boolean;
	boldTitle?: boolean;
	allowEndChat?: boolean;
	allowChatTransfer?: boolean;
	endChatHandler?: (() => void) | null;
	transferChatHandler?: ((userId: string) => void) | null;
	disableEndChat?: boolean;
	disableTransferChat?: boolean;
}

const Header: FC<props> = ({
	user,
	actionsHandler,
	allowClose,
	allowResize,
	allowPrevious,
	boldTitle,
	allowEndChat,
	endChatHandler,
	allowChatTransfer,
	transferChatHandler,
	disableEndChat,
	disableTransferChat,
}: props): JSX.Element => {
	const [isFrameExpanded, setIsFrameExpanded] = useState(Utils.isFrameExpanded());
	const [deviceType, setDeviceType] = useState(Utils.getDeviceType());
	const [anchorForTransferMenu, setAnchorForTransferMenu] = useState<HTMLElement | null>();
	const [pageForTransferUser, setPageforTransferUsers] = useState<number>(0);
	const [tranferUsersList, setTransferUsersList] = useState<ParsedUser[]>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [expandChatbotWindowState, setExpandChatbotWindowState] = useState<boolean>(false);

	const transferUserMenu = Boolean(anchorForTransferMenu);

	/** ******************************************** Helper functions *************************************************** */

	const fetchTransferUsers = async (directPage?: number) => {
		try {
			setIsLoading(true);
			const response = await ApiService.fetchUsers({
				page: directPage ? directPage + 1 : pageForTransferUser + 1,
				limit: 5,
				role: USER_ROLES.AGENT,
				status: STATUS.ACTIVE,
			});
			if (pageForTransferUser > 0) {
				setTransferUsersList((existingUsers) => existingUsers?.concat(response.data));
			} else {
				setTransferUsersList(response.data);
			}
		} catch (error) {
			LoggerService.logError(error, 'failed to load for transfer agents dropdown list');
		} finally {
			setIsLoading(false);
		}
	};

	const openTransferUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorForTransferMenu(event.currentTarget);
	};

	const closeTransferMenu = () => {
		setAnchorForTransferMenu(null);
	};

	const closeChatWindow = () => {
		UIService.toggleChatWindow();
		if (actionsHandler) {
			actionsHandler('CLOSE_WIDGET');
		}
	};

	const handleResize = () => PushNotificationService.sendFullScreenExpansionNotification();

	/** ******************************************** UseEffects *************************************************** */

	useEffect(() => {
		const resizeHandler = () => {
			setDeviceType(Utils.getDeviceType());
			setIsFrameExpanded(Utils.isFrameExpanded());
		};
		const transferUserInitialSetup = async () => {
			setPageforTransferUsers(0);
			setTransferUsersList([]);
			fetchTransferUsers();
		};
		window.addEventListener('resize', resizeHandler);
		if (allowChatTransfer) {
			transferUserInitialSetup();
		}
		return () => window.removeEventListener('resize', resizeHandler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	useEffect(() => {
		if (allowChatTransfer) {
			setPageforTransferUsers((newPage) => {
				fetchTransferUsers(newPage);
				return newPage;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageForTransferUser]);

	// this use effect is used to inject the dynamic class for the chatbot title
	// 'chatbot_title_style' key in the org_config object of the '/public' API response contains the style information
	useEffect(() => {
		const style = StorageService?.config?.org_config?.chatbot_title_style;
		if (!style) return;

		const fontUrl = style['font-family-url'];
		// appending the font url to the document head if it exists
		if (fontUrl) {
			const existing = document.querySelector(`link[href="${fontUrl}"]`);
			if (!existing) {
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = fontUrl;
				document.head.appendChild(link);
			}
		}

		// Inject dynamic class only once
		const dynamicClass = 'chatbot-title-dynamic';
		if (!document.getElementById(dynamicClass)) {
			const styleTag = document.createElement('style');
			styleTag.id = dynamicClass;

			styleTag.innerHTML = `
			.${dynamicClass} {
				${style['font-family'] ? `font-family: '${style['font-family']}', sans-serif;` : ''}
				${style['font-size'] ? `font-size: ${style['font-size']};` : ''}
				${style['font-weight'] ? `font-weight: ${style['font-weight']};` : ''}
				${style['letter-spacing'] ? `letter-spacing: ${style['letter-spacing']};` : ''}
			}
		`;

			document.head.appendChild(styleTag);
		}
	}, []);

	// adding classname to header title based on the conditions mentined
	const chatbotHeaderTitleClassName = useMemo(() => {
		let titleClassName = 'title';

		// class name for bold header title
		if (boldTitle) {
			titleClassName = 'chat-title';
		}

		// this functionality is controlled by 'chatbot_title_style' key in the org_config object of the '/public' API response
		const chatbotTitleStyle = StorageService?.config?.org_config?.chatbot_title_style;
		if (chatbotTitleStyle) {
			titleClassName += ' chatbot-title-dynamic'; // space is needed before the class name
		}

		return titleClassName;
	}, [boldTitle]);

	/** ******************************************** UI Helpers *************************************************** */

	const userDetailsTitle = (
		<div className="title-container">
			{allowPrevious && (
				<Icon
					onClick={() => {
						closeChatWindow();
					}}
					className="hide-on-expanded-frame header-icon chevron"
					type="chevron-left"
				/>
			)}
			{user?.name && (
				<>
					<Avatar
						className="name-avatar"
						src={user?.imageUrl}
						round
						size="40"
						name={user?.name}
						title=""
						maxInitials={3}
					/>
					<Text className={chatbotHeaderTitleClassName}>
						{Utils.getTrimmedText(user?.name, 15)}
					</Text>
				</>
			)}
		</div>
	);

	const generalTitle = (
		<div className="title-container">
			<Text className="title">Chats</Text>
		</div>
	);

	const resizeButtons = (
		<i
			tabIndex={0}
			onClick={() => handleResize()}
			className={`header-icon  ${isFrameExpanded ? 'isc-collapse-alt' : 'isc-expand-alt'}  ${
				deviceType === DEVICE_TYPE.MOBILE ? 'hide' : ''
			}`}
			onKeyUp={(event) => {
				if (event.key === 'Enter') handleResize();
			}}
			aria-label="resize"
			role="button"
		/>
	);

	const closeButton = (
		<i
			tabIndex={0}
			onClick={() => PushNotificationService.sendCloseNotification({ mouseClick: true })}
			onKeyUp={(event) => {
				if (event.key === 'Enter')
					PushNotificationService.sendCloseNotification({ mouseClick: true });
			}}
			className="isc-times-solid header-icon"
			aria-label="close"
			role="button"
		/>
	);

	const transferAgentMenuAndButton = (
		<>
			<Tooltip title="Tranfer to another agent. Please make sure the agent is online.">
				<Button
					sx={{ ml: 1, textTransform: 'capitalize' }}
					size="small"
					id="transfer-chat-button"
					onClick={openTransferUserMenu}
					disabled={disableTransferChat}
					variant="contained"
					startIcon={<GroupIcon />}>
					Transfer
				</Button>
			</Tooltip>
			<Menu
				id="basic-menu"
				anchorEl={anchorForTransferMenu}
				open={transferUserMenu}
				onClose={closeTransferMenu}
				sx={{ height: 500 }}
				MenuListProps={{
					'aria-labelledby': 'basic-button',
				}}>
				{!tranferUsersList?.length && <MenuItem> No users </MenuItem>}
				{tranferUsersList?.length && (
					<>
						{tranferUsersList.map((agent) => (
							<MenuItem
								onClick={() => {
									if (transferChatHandler) transferChatHandler(agent._id);
									closeTransferMenu();
								}}>
								{agent.firstName} {agent.lastName}
							</MenuItem>
						))}
						<MenuItem
							sx={{ justifyContent: 'center' }}
							onClick={() => setPageforTransferUsers((currentPage) => currentPage + 1)}>
							{isLoading ? <Loader size="small" /> : 'Show More'}
						</MenuItem>
					</>
				)}
			</Menu>
		</>
	);

	const EndChatButton = (
		<Tooltip title="End the conversation">
			<Button
				sx={{ ml: 1, textTransform: 'capitalize' }}
				size="small"
				id="end-chat-button"
				tabIndex={0}
				variant="contained"
				disabled={disableEndChat}
				onClick={() => {
					if (endChatHandler) endChatHandler();
				}}>
				End
			</Button>
		</Tooltip>
	);

	/**
	 * A component that renders an icon to expand or collapse the chatbot window
	 * depending on the state of the chatbot window. The icon is only rendered if
	 * the expand_chatbot_window config is true and the device type is not mobile.
	 * The icon is a ZoomInMapIcon if the chatbot window is expanded, and a
	 * ZoomOutMapIcon if the chatbot window is collapsed. Clicking on the icon
	 * toggles the state of the chatbot window.
	 */
	const ExpandChatbotWindowIcon = () => {
		if (
			!StorageService?.config?.org_config?.expand_chatbot_window ||
			deviceType === DEVICE_TYPE.MOBILE
		) {
			return null;
		}

		/**
		 * Toggles the expansion state of the chatbot window and sends a notification
		 * to the parent iFrame about the new state.
		 * @param expand A boolean indicating whether to expand (true) or collapse (false) the chatbot window.
		 */
		const handleToggle = (expand: boolean) => {
			setExpandChatbotWindowState(expand);
			PushNotificationService.sendChatbotWindowExpansionNotification(expand);
		};

		return expandChatbotWindowState ? (
			<ZoomInMapIcon
				className="header-icon"
				onClick={() => handleToggle(false)}
				aria-label="Collapse chatbot window"
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleToggle(false);
				}}
			/>
		) : (
			<ZoomOutMapIcon
				className="header-icon"
				onClick={() => handleToggle(true)}
				aria-label="Expand chatbot window"
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleToggle(true);
				}}
			/>
		);
	};

	return (
		<Box className="header-container">
			<Box className="title-container">{user ? userDetailsTitle : generalTitle}</Box>
			<Box sx={{ display: user ? 'flex' : { sm: 'flex', md: 'none' }, flexDirection: 'row' }}>
				{allowChatTransfer && user && transferAgentMenuAndButton}
				{allowEndChat && user && EndChatButton}
				{allowResize && resizeButtons}
				<ExpandChatbotWindowIcon />
				{allowClose && closeButton}
			</Box>
		</Box>
	);
};

Header.defaultProps = {
	user: null,
	actionsHandler: null,
	allowClose: false,
	allowResize: false,
	allowPrevious: false,
	boldTitle: false,
	allowEndChat: false,
	endChatHandler: null,
	allowChatTransfer: false,
	transferChatHandler: null,
	disableEndChat: false,
	disableTransferChat: false,
};

export default Header;
